import { InventoryQuantity } from "./InventoryContext";

/**
 * Calculates how many complete cycles a machine can perform based on progress and cycle time.
 *
 * @param progressMs - Current progress in milliseconds
 * @param cycleTimeMs - Time required for one complete cycle in milliseconds
 * @returns Number of complete cycles ready to execute
 *
 * @example
 * const cycles = calculateCompleteCycles(8500, 2000);
 * // Returns 4 (8500 / 2000 = 4.25, floored to 4)
 */
export const calculateCompleteCycles = (progressMs: number, cycleTimeMs: number): number => {
    return Math.floor(progressMs / cycleTimeMs);
};

/**
 * Calculates the maximum number of cycles possible based on available fuel.
 *
 * @param fuelInventory - Inventory entry for the fuel item (undefined if no fuel required)
 * @param fuelCostPerCycle - Amount of fuel consumed per cycle
 * @param maxCycles - Maximum cycles to cap at (e.g., cycles ready from progress)
 * @returns Maximum cycles possible with available fuel, or maxCycles if fuel is unlimited
 *
 * @example
 * const fuelEntry = { item: coalItem, quantity: 50 };
 * const maxCycles = calculateMaxCyclesByFuel(fuelEntry, 2, 10);
 * // Returns 10 (min of 50/2=25 and 10)
 */
export const calculateMaxCyclesByFuel = (
    fuelInventory: InventoryQuantity | undefined,
    fuelCostPerCycle: number,
    maxCycles: number
): number => {
    if (!fuelInventory) return maxCycles;
    const maxByFuel = Math.floor(fuelInventory.quantity / fuelCostPerCycle);
    return Math.min(maxCycles, maxByFuel);
};

/**
 * Calculates the maximum number of cycles possible based on available input resources.
 *
 * @param inputInventory - Inventory entry for the input item
 * @param inputCostPerCycle - Amount of input consumed per cycle
 * @param maxCycles - Maximum cycles to cap at
 * @returns Maximum cycles possible with available input
 *
 * @example
 * const ironOre = { item: ironOreItem, quantity: 100 };
 * const maxCycles = calculateMaxCyclesByInput(ironOre, 1, 50);
 * // Returns 50 (min of 100/1=100 and 50)
 */
export const calculateMaxCyclesByInput = (
    inputInventory: InventoryQuantity,
    inputCostPerCycle: number,
    maxCycles: number
): number => {
    const maxByInput = Math.floor(inputInventory.quantity / inputCostPerCycle);
    return Math.min(maxCycles, maxByInput);
};

/**
 * Consumes fuel from inventory for a given number of cycles.
 *
 * @param fuelInventory - Inventory entry for the fuel (can be undefined if no fuel required)
 * @param fuelCostPerCycle - Amount of fuel consumed per cycle
 * @param cycles - Number of cycles executed
 * @returns void (mutates fuelInventory.quantity)
 *
 * @example
 * const coal = { item: coalItem, quantity: 50 };
 * consumeFuel(coal, 2, 10);
 * // coal.quantity is now 30 (50 - 2*10)
 */
export const consumeFuel = (
    fuelInventory: InventoryQuantity | undefined,
    fuelCostPerCycle: number,
    cycles: number
): void => {
    if (fuelInventory) {
        fuelInventory.quantity -= fuelCostPerCycle * cycles;
    }
};

/**
 * Consumes input resources from inventory for a given number of cycles.
 *
 * @param inputInventory - Inventory entry for the input resource
 * @param inputCostPerCycle - Amount of input consumed per cycle
 * @param cycles - Number of cycles executed
 * @returns void (mutates inputInventory.quantity)
 *
 * @example
 * const ironOre = { item: ironOreItem, quantity: 100 };
 * consumeInput(ironOre, 1, 50);
 * // ironOre.quantity is now 50
 */
export const consumeInput = (
    inputInventory: InventoryQuantity,
    inputCostPerCycle: number,
    cycles: number
): void => {
    inputInventory.quantity -= inputCostPerCycle * cycles;
};

/**
 * Produces output resources into inventory for a given number of cycles.
 *
 * @param outputInventory - Inventory entry for the output resource
 * @param outputPerCycle - Amount of output produced per cycle
 * @param cycles - Number of cycles executed
 * @returns void (mutates outputInventory.quantity)
 *
 * @example
 * const ironPlate = { item: ironPlateItem, quantity: 10 };
 * produceOutput(ironPlate, 1, 50);
 * // ironPlate.quantity is now 60
 */
export const produceOutput = (
    outputInventory: InventoryQuantity,
    outputPerCycle: number,
    cycles: number
): void => {
    outputInventory.quantity += outputPerCycle * cycles;
};

/**
 * Calculates remaining progress after executing cycles.
 *
 * @param currentProgressMs - Current progress in milliseconds
 * @param cycleTimeMs - Time for one complete cycle in milliseconds
 * @returns Remaining progress (currentProgress modulo cycleTimeMs)
 *
 * @example
 * const remaining = calculateRemainingProgress(8500, 2000);
 * // Returns 500 (8500 % 2000)
 */
export const calculateRemainingProgress = (
    currentProgressMs: number,
    cycleTimeMs: number
): number => {
    return currentProgressMs % cycleTimeMs;
};

/**
 * Checks if a machine has sufficient fuel to operate.
 *
 * @param fuelInventory - Inventory entry for the fuel (undefined if no fuel required)
 * @param requiredQuantity - Minimum fuel quantity required
 * @returns True if fuel is sufficient or not required, false otherwise
 *
 * @example
 * const coal = { item: coalItem, quantity: 5 };
 * hasSufficientFuel(coal, 2); // true
 * hasSufficientFuel(coal, 10); // false
 * hasSufficientFuel(undefined, 0); // true (no fuel required)
 */
export const hasSufficientFuel = (
    fuelInventory: InventoryQuantity | undefined,
    requiredQuantity: number
): boolean => {
    if (!fuelInventory) return true; // No fuel required
    return fuelInventory.quantity >= requiredQuantity;
};
